import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchDevices, fetchTariffDataByBuildingId, fetchSubscriptionByBuildingId, activateUserSubscription, cancelUserSubscription } from "../../services/api";
import colors from "../../theme";
import SnackbarComp from "../../components/Snackbar/SnackbarComp";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const Tariffs = () => {
  const location = useLocation();
    const navigate = useNavigate();
  
    const [error, setError] = useState(null);
    const [statusCode, setStatusCode] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);

    const [tariffData, setTariffData] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState("");
    const [chargingPoints, setChargingPoints] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState("");
    const isSmallScreen = useMediaQuery("(max-width:1000px)");
    const currency = useSelector((state) => state.currency.currency) || "";
    const [platformFee, setPlatformFee] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [configuredPeriodDays, setConfiguredPeriodDays] = useState(30);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
    const [openUnsubscribeDialog, setOpenUnsubscribeDialog] = useState(false);

  useEffect(() => {
   const fetchUserBuildingsAndTariffs = async () => {
    setIsCheckingStatus(true);
    try {
        const devices = (await fetchDevices()) || [];
        setChargingPoints(devices);

        if (devices.length > 0) {
          const firstDevice = devices[0];
          setSelectedDevice(firstDevice.chargePointId);
          setSelectedBuilding({
            buildingId: firstDevice.buildingId,
            buildingName: firstDevice.buildingName,
          });
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message);
        setStatusCode(err.response?.status || 500);
        setOpenSnackbar(true);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    fetchUserBuildingsAndTariffs();
  }, []);

  useEffect(() => {
    if (!selectedBuilding?.buildingId) return;

    const getLatestTariff = async () => {
      try {
        
        const response = await fetchTariffDataByBuildingId(selectedBuilding.buildingId);

        if (!response || response.length === 0) {
          console.warn("No tariff data found for building:", selectedBuilding.buildingId);
          setTariffData([]);
          setPlatformFee(null);
          setSubscription(null);
          return;
        }

        const currentDate = new Date();

        const currentTariff = response.filter((tariff) => (new Date(tariff.startDate) <= currentDate && new Date(tariff.endDate) >= currentDate))[0];

        if (currentTariff) {
          const formatted = {
            onPeak: currentTariff.peakRate,
            offPeak: currentTariff.offPeakRate,
            hours: (currentTariff.peakHours || []).map(h => `${h.startTime} - ${h.endTime}`),
            applicable: currentTariff.applicableDays.reduce((acc, dayNum) => {
              const dayMap = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
              acc[dayMap[parseInt(dayNum)]] = true;
              return acc;
            }, {}),
            startDate: currentTariff.startDate,
            endDate: currentTariff.endDate,
          };

          setTariffData([formatted]);
        }
        else {
          setTariffData([]);
        }

        try {
          const overview = await fetchSubscriptionByBuildingId(selectedBuilding.buildingId);
          setPlatformFee(overview?.platformFee || currentTariff?.platformFee || null);
          setSubscription(overview?.subscription || null);
          setConfiguredPeriodDays(Number(overview?.subscriptionPeriodDays) || 30);
        } catch (subscriptionError) {
          setPlatformFee(currentTariff?.platformFee || null);
          setSubscription(null);
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message);
        setOpenSnackbar(true);
      }
    };

    getLatestTariff();
  }, [selectedBuilding]);
  
  useEffect(() => {
    const { message = "", status = 200, redirect } = location.state || {};
    if (message) {
      setError(message);
      setStatusCode(status);
      setOpenSnackbar(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (redirect) {
      setTimeout(() => { navigate('/'); }, 2000);
    }
  }, [location, navigate]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleDeviceChange = (event) => {
    const deviceId = event.target.value;
    setSelectedDevice(deviceId);

    const deviceObj = chargingPoints.find((p) => p.chargePointId === deviceId);
    if (deviceObj) {
      setSelectedBuilding({
        buildingId: deviceObj.buildingId,
        buildingName: deviceObj.buildingName,
      });
    }
  };

  const paygOffered = platformFee?.paygRate !== null && platformFee?.paygRate !== undefined;
  const inEffectSubscription = !!subscription && subscription.status === "ACTIVE";
  const subscriptionOffered =
    (platformFee?.subscriptionFee !== null && platformFee?.subscriptionFee !== undefined) || inEffectSubscription;
  const subscriptionOnly = subscriptionOffered && !paygOffered;
  const displaySubscriptionPrice =
    inEffectSubscription && subscription?.price != null && subscription?.price !== undefined
      ? subscription.price
      : platformFee?.subscriptionFee;
  const formatFee = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toFixed(2) : value;
  };
  const formatDate = (value) => (value ? dayjs(value).format("D/M/YYYY") : "-");
  const inEffectPeriodDays = inEffectSubscription && subscription?.subscriptionDate && (subscription.expiryDate || subscription.periodEnd)
    ? Math.max(1, dayjs(subscription.expiryDate || subscription.periodEnd).diff(dayjs(subscription.subscriptionDate), "day"))
    : null;
  const subscriptionPeriodDays = inEffectPeriodDays ?? configuredPeriodDays;
  
  const cancelledPendingRenewal = inEffectSubscription && !subscription?.autoRenewal;
  const daysRemainingOnSubscription = inEffectSubscription
    ? Math.max(0, dayjs(subscription.expiryDate || subscription.periodEnd).startOf("day").diff(dayjs().startOf("day"), "day"))
    : null;
  const daysUntilPaygActivation = cancelledPendingRenewal ? daysRemainingOnSubscription : null;

  const handleActivateSubscription = async () => {
    if (!selectedBuilding?.buildingId) {
      return;
    }
    setSubscriptionLoading(true);
    try {
      const response = await activateUserSubscription(selectedBuilding.buildingId);
      setSubscription(response?.data || response);
      setError(response?.message || "Subscription activated");
      setStatusCode(response?.status || 201);
      setOpenSnackbar(true);
      setOpenSubscribeDialog(false);
    } catch (err) {
      setError(err.message || "Unable to activate subscription");
      setStatusCode(err.status || 500);
      setOpenSnackbar(true);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleSubscribeClick = () => {
    if (!selectedBuilding?.buildingId) {
      return;
    }
    setOpenSubscribeDialog(true);
  };

  const handleDeactivateSubscription = async () => {
    if (!subscription?.id) {
      return;
    }
    setSubscriptionLoading(true);
    try {
      const response = await cancelUserSubscription(subscription.id);
      setSubscription(response?.data || response);
      setError(response?.message || "Subscription cancelled");
      setStatusCode(response?.status || 200);
      setOpenSnackbar(true);
      setOpenUnsubscribeDialog(false);
    } catch (err) {
      setError(err.message || "Unable to cancel subscription");
      setStatusCode(err.status || 500);
      setOpenSnackbar(true);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleUnsubscribeClick = () => {
    if (!subscription?.id) {
      return;
    }
    setOpenUnsubscribeDialog(true);
  };

  const tariff = tariffData[0] || { onPeak: "", offPeak: "", hours: [], applicable: {} };
  const daysOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (isCheckingStatus) {
    return (
      <Box
        component="main"
        sx={{ flex: 1, py: 6, px: 4, bgcolor: colors.mainContentBgColor }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box component="main"
        sx={{
          flex: 1,
          px: 0,
          bgcolor: colors.mainContentBgColor,
          overflow: "hidden",
        }}
      >
        <Box component="form"
          sx={{
            display: "flex",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 0, md: "1rem" },
            bgcolor: colors.mainContentBgColor,
            m: "0rem 1rem",
            mt: 2,
            pt: 1,
          }}
        >
          <FormControl
            variant="standard"
            fullWidth
            sx={{
              width: isSmallScreen ? "FullWidth" : "12rem",
              mr: { xs: 0, md: 1 },
              "& .MuiInput-underline:before": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: colors.secondary,
              },
            }}
          >
            <InputLabel id="device-select-label">Device</InputLabel>
            <Select
              labelId="device-select-label"
              value={selectedDevice}
              onChange={handleDeviceChange}
              label="Device"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: "#eaeff1",
                  },
                },
              }}
            >
              {chargingPoints.map((point) => (
                <MenuItem
                  key={point.chargePointId}
                  value={point.chargePointId}
                >
                  {point.chargePointId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            m: "1.5rem 1rem",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper elevation={0}
            sx={{
              width: "100%",
              maxWidth: 920,
              bgcolor: colors.mainContentBgColor,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2.5 },
            }}
          >
            <Box sx={{ mt: 1, mb: 4 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.75 , textAlign: 'center'}}>
                Peak Days
              </Typography>

              <ToggleButtonGroup
                value={daysOrder.filter((d) => tariff.applicable[d])}
                exclusive={false}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  "& .MuiToggleButton-root": {
                    borderRadius: "50%",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: 12, sm: 13 },
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    minWidth: "auto",
                    padding: 0,
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&.Mui-selected": {
                      backgroundColor: colors.secondary,
                      color: colors.text,
                      "&:hover": {
                        backgroundColor: colors.secondary,
                      },
                    },
                    "&:not(.Mui-selected)": {
                      backgroundColor: "transparent",
                      color: "text.secondary",
                    },
                  },
                }}
              >
              {daysOrder.map((d) => {
                const applicable = !!tariff.applicable[d];
                return (
                  <ToggleButton
                    key={d}
                    value={d}
                    selected={applicable}
                    disableRipple
                    sx={{
                      borderRadius: "50%",
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      minWidth: "auto",
                      padding: 0,
                      ...(applicable
                        ? { backgroundColor: colors.secondary, color: colors.text }
                        : { backgroundColor: "transparent", color: "text.secondary" }),
                    }}
                  >
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 700 }}>
                      {d}
                    </Typography>
                  </ToggleButton>
                );
              })}
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 , textAlign: 'center'}}>
                Peak Hours
              </Typography>
              <Box sx={{ mt: 0.5, textAlign: 'center' }}>
                {tariff.hours.map((h, idx) =>
                  h ? (
                    <Typography
                      key={idx}
                      sx={{
                        fontSize: 15,
                        lineHeight: 1.25,
                        mb: idx === tariff.hours.length - 1 ? 0 : 0.5,
                      }}
                    >
                      {h}
                    </Typography>
                  ) : null
                )}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 14, mt: 2, mb: 0.5 , textAlign: 'center'}}>
                  For the rest of hours off-peak rates would be applied.
                </Typography>
              </Box>
            </Box>
            <Box
                sx={{
                  gap: { xs: 1.5, sm: 3 },
                  alignItems: "center",
                  flexWrap: "wrap",
                  mb: 3,
                }}
            >
              <Typography sx={{ fontSize: 20, fontWeight: 700, textAlign: 'center'}}>
                Rates
              </Typography>
                <Box
                  sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 6,
                  width: "100%",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "text.secondary" }}>
                    Peak
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                    {tariff.onPeak}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "text.secondary" }}>
                    Off-Peak
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                    {tariff.offPeak}
                  </Typography>
                </Box>
              </Box>
            </Box>
                
          </Paper>
        </Box>
        {(paygOffered || subscriptionOffered) && (
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, textAlign: "center", mb: 2, width: "100%", color: colors.primary}}>
                    Platform Fee
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 920,
                      px: { xs: 2, sm: 2, md: 0},
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    {paygOffered && (
                      <Box
                        sx={{
                          flex: 1,
                          border: !inEffectSubscription ? `3px solid ${colors.primary}` : "1px solid #cfd8dc",
                          borderRadius: 2,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography sx={{ color: colors.secondary, fontWeight: 700, textAlign: "center", py: 1,position: "relative",zIndex: 1 }}>
                          Pay As You Go
                        </Typography>
                        <Box sx={{ bgcolor: colors.mainContentBgColor, py: 2, textAlign: "center", flex: 1, display: "flex", flexDirection: "column", transform: "translateY(-1rem)" }}>
                          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, color: colors.secondary }}>
                              {formatFee(platformFee?.paygRate)}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                              {currency} per kWh
                            </Typography>
                          </Box>
                          {cancelledPendingRenewal && (
                            <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                              {`Will be activated in ${daysUntilPaygActivation} day${daysUntilPaygActivation === 1 ? "" : "s"}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    {subscriptionOffered && (
                      <Box
                        sx={{
                          flex: 1,
                          border: inEffectSubscription ? `3px solid ${colors.primary}` : "1px solid #cfd8dc",
                          borderRadius: 2,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography sx={{ color: colors.secondary, fontWeight: 700, textAlign: "center", py: 1 }}>
                          Monthly Subscription
                        </Typography>
                        <Box sx={{ bgcolor: colors.mainContentBgColor, py: 2, textAlign: "center", flex: 1 }}>
                          <Typography sx={{ fontSize: 28, fontWeight: 800, color: colors.secondary }}>
                            {formatFee(displaySubscriptionPrice)}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            {currency} / {subscriptionPeriodDays} days
                          </Typography>
                          {!subscriptionOnly && (inEffectSubscription && subscription?.autoRenewal ? (
                            <Button
                              variant="contained"
                              disabled={subscriptionLoading}
                              onClick={handleUnsubscribeClick}
                              sx={{
                                mt: 1.5,
                                bgcolor: colors.secondary,
                                color: colors.text,
                                "&:hover": { bgcolor: colors.btnBackgroundColorOnHover },
                                "&.Mui-disabled": { bgcolor: "#9e9e9e", color: "#fff" },
                              }}
                            >
                              {"Unsubscribe"}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              disabled={subscriptionLoading || !selectedBuilding?.buildingId}
                              onClick={handleSubscribeClick}
                              sx={{
                                mt: 1.5,
                                bgcolor: colors.secondary,
                                color: colors.text,
                                "&:hover": { bgcolor: colors.btnBackgroundColorOnHover },
                                "&.Mui-disabled": { bgcolor: "#9e9e9e", color: "#fff" },
                              }}
                            >
                              {"Subscribe"}
                            </Button>
                          ))}
                          {inEffectSubscription && (
                            <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                              {!subscription?.autoRenewal
                                ? `Cancelled - active until ${formatDate(subscription.expiryDate || subscription.periodEnd)}`
                                : `Ends on ${formatDate(subscription.expiryDate || subscription.periodEnd)}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
      </Box>
      <SnackbarComp open={openSnackbar} onClose={handleCloseSnackbar} errorMessage={error} statusCode={statusCode} />
      <Dialog
        open={openSubscribeDialog}
        onClose={() => !subscriptionLoading && setOpenSubscribeDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.mainContentBgColor,
          },
        }}
      >
        <DialogTitle>Subscribe?</DialogTitle>
        <DialogContent>
          <DialogContentText>
          {cancelledPendingRenewal ? (
            <>
              Do you want to enable auto-renewal of your subscription again? No amount will be deducted
              immediately, and you will be charged on {dayjs(subscription?.expiryDate).format("D MMMM")}.
            </>
          ) : (
            <>
              Do you want to purchase a subscription for {subscriptionPeriodDays} day
              {subscriptionPeriodDays === 1 ? "" : "s"}? This will deduct {currency}{" "}
              <strong>{formatFee(displaySubscriptionPrice)}</strong>{" "}
              as a platform fee from your wallet. The electricity tariff will still be charged from your
              wallet based on usage.
            </>
          )}
          </DialogContentText>
        </DialogContent>
        
        <DialogActions>
          <Button
            onClick={() => setOpenSubscribeDialog(false)}
            disabled={subscriptionLoading}
            sx={{ color: colors.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleActivateSubscription}
            autoFocus
            disabled={subscriptionLoading}
            sx={{
              color: colors.text,
              backgroundColor: colors.secondary,
              "&:hover": {
                backgroundColor: colors.btnBackgroundColorOnHover,
              },
            }}
          >
            {subscriptionLoading ? <CircularProgress size={24} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openUnsubscribeDialog}
        onClose={() => !subscriptionLoading && setOpenUnsubscribeDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.mainContentBgColor,
          },
        }}
      >
        <DialogTitle>Unsubscribe?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to cancel auto-renewal of your subscription? Your current subscription will expire on{" "}
            {dayjs(subscription?.expiryDate).format("D MMMM")}.
            {paygOffered && " After that, you will switch back to Pay As You Go model."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenUnsubscribeDialog(false)}
            disabled={subscriptionLoading}
            sx={{ color: colors.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateSubscription}
            autoFocus
            disabled={subscriptionLoading}
            sx={{
              color: colors.text,
              backgroundColor: colors.secondary,
              "&:hover": {
                backgroundColor: colors.btnBackgroundColorOnHover,
              },
            }}
          >
            {subscriptionLoading ? <CircularProgress size={24} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Tariffs;